import { h, Component } from 'preact';

function countryCodeToEmoji(code) {
    if (!code || code.length !== 2) return '';
    return String.fromCodePoint(
        ...Array.from(code.toUpperCase()).map(c => c.charCodeAt(0) + 127397),
    );
}

const MEDAL_COLORS = ['#FFD700', '#C0C0C0', '#CD7F32'];
const MEDAL_ICONS = ['fa-solid fa-trophy', 'fa-solid fa-medal', 'fa-solid fa-medal'];
const SHOW_LIMIT = 50;

class ResultsList extends Component {
    constructor(props) {
        super(props);
        this.state = {
            search: '',
            showAll: false,
        };
    }

    onSearchInput = (e) => {
        this.setState({ search: e.target.value, showAll: false });
    }

    toggleShowAll = () => {
        this.setState(prev => ({ showAll: !prev.showAll }));
    }

    render() {
        const { resultsList } = this.props;
        if (!resultsList) return null;

        const { search, showAll } = this.state;
        const entries = resultsList.entries || [];

        // Only show podium for entries that actually have position 1, 2, or 3
        const first = entries.find(e => e.position === 1 || e.position === '1');
        const second = entries.find(e => e.position === 2 || e.position === '2');
        const third = entries.find(e => e.position === 3 || e.position === '3');
        const podiumCandidates = [first, second, third].filter(Boolean);
        const hasPodium = podiumCandidates.length > 0;

        // Filter entries by search
        const searchLower = search.toLowerCase().trim();
        const filtered = searchLower
            ? entries.filter(e =>
                (e.name && e.name.toLowerCase().includes(searchLower)) ||
                (e.club && e.club.toLowerCase().includes(searchLower)) ||
                (e.country && e.country.toLowerCase().includes(searchLower)))
            : entries;

        // Determine what to display in the list (exclude podium entries if no search)
        const podiumPositions = new Set(podiumCandidates.map(e => Number(e.position)));
        const listEntries = searchLower ? filtered : filtered.filter(e => !hasPodium || !podiumPositions.has(Number(e.position)));
        const visibleList = showAll || searchLower ? listEntries : listEntries.slice(0, SHOW_LIMIT);
        const hasMore = !showAll && !searchLower && listEntries.length > SHOW_LIMIT;

        // Podium order: 2nd, 1st, 3rd (visual layout)
        const podiumEntries = hasPodium && !searchLower ? [second, first, third].filter(Boolean) : null;

        return (
            <div class='my-4 p-3 rounded-3 bg-body-secondary'>
                {/* Header */}
                <div class='d-flex align-items-center mb-3'>
                    {resultsList.icon && (
                        <i class={`${resultsList.icon} me-2`} style='font-size: 1.3em;' />
                    )}
                    <h5 class='mb-0 flex-fill fw-bold'>{resultsList.title || 'Resultater'}</h5>
                    {resultsList.category && (
                        <span class='badge text-bg-primary ms-2'>{resultsList.category}</span>
                    )}
                </div>

                {/* Podium for top 3 */}
                {podiumEntries && (
                    <div class='d-flex justify-content-center align-items-end mb-4 gap-2'>
                        {podiumEntries.map((entry) => {
                            const pos = Number(entry.position);
                            const actualPos = pos - 1; // 0=gold, 1=silver, 2=bronze
                            const isFirst = pos === 1;
                            const photoSize = isFirst ? '72px' : '56px';

                            return (
                                <div
                                    key={entry.position}
                                    class='d-flex flex-column align-items-center text-center'
                                    style={`flex: 1; max-width: 140px; ${isFirst ? 'margin-bottom: 20px;' : ''}`}
                                >
                                    {/* Photo or numbered circle */}
                                    {entry.imageUrl ? (
                                        <img
                                            src={entry.imageUrl}
                                            alt={entry.name}
                                            class='rounded-circle mb-2 border border-3'
                                            style={`width: ${photoSize}; height: ${photoSize}; object-fit: cover; border-color: ${MEDAL_COLORS[actualPos]} !important;`}
                                        />
                                    ) : (
                                        <div
                                            class='rounded-circle mb-2 d-flex align-items-center justify-content-center fw-bold text-white'
                                            style={`width: ${photoSize}; height: ${photoSize}; background: ${MEDAL_COLORS[actualPos]}; font-size: ${isFirst ? '1.5em' : '1.2em'}; ${actualPos === 0 ? 'color: #333 !important;' : ''}`}
                                        >
                                            {entry.position}
                                        </div>
                                    )}
                                    {/* Country flag */}
                                    {entry.country && (
                                        <span style='font-size: 1.1em;'>{countryCodeToEmoji(entry.country)}</span>
                                    )}
                                    {/* Name */}
                                    <small class='fw-bold text-body'>{entry.name}</small>
                                    {/* Club */}
                                    {entry.club && (
                                        <small class='text-body-secondary' style='font-size: 0.75em;'>{entry.club}</small>
                                    )}
                                    {/* Medal + result */}
                                    <div class='d-flex align-items-center gap-1 mt-1'>
                                        <i class={MEDAL_ICONS[actualPos]} style={`color: ${MEDAL_COLORS[actualPos]};`} />
                                        <small class='text-body-secondary'>{entry.result}</small>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Search bar */}
                {entries.length > 5 && (
                    <div class='mb-3'>
                        <input
                            type='text'
                            class='form-control form-control-sm'
                            placeholder='Sok etter navn, klubb eller land...'
                            value={search}
                            onInput={this.onSearchInput}
                        />
                    </div>
                )}

                {/* Results table */}
                {visibleList.length > 0 && (
                    <div class='list-group list-group-flush'>
                        {visibleList.map(entry => {
                            const isTop3 = entry.position <= 3;
                            return (
                                <div
                                    key={entry.position}
                                    class='list-group-item d-flex align-items-center px-2 py-2'
                                    style={isTop3 ? `border-left: 3px solid ${MEDAL_COLORS[entry.position - 1]};` : ''}
                                >
                                    <span
                                        class='fw-bold text-body-secondary me-3 text-end'
                                        style='min-width: 28px; font-size: 0.85em;'
                                    >
                                        {entry.position}
                                    </span>
                                    {entry.country && (
                                        <span class='me-2' style='font-size: 1em;'>{countryCodeToEmoji(entry.country)}</span>
                                    )}
                                    <span class='flex-fill text-body' style='font-size: 0.9em;'>
                                        {entry.name}
                                        {entry.club && (
                                            <small class='text-body-secondary ms-2'>({entry.club})</small>
                                        )}
                                    </span>
                                    <span class='text-body-secondary text-end' style='font-size: 0.85em; min-width: 70px;'>
                                        {entry.result}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Show all toggle */}
                {hasMore && (
                    <div class='text-center mt-2'>
                        <button
                            type='button'
                            class='btn btn-sm btn-outline-secondary rounded-pill'
                            onClick={this.toggleShowAll}
                        >
                            Vis alle ({listEntries.length})
                        </button>
                    </div>
                )}

                {searchLower && filtered.length === 0 && (
                    <div class='text-center text-body-secondary py-3'>
                        <small>Ingen resultater funnet for "{search}"</small>
                    </div>
                )}
            </div>
        );
    }
}

export default ResultsList;
